import React, { useState, useEffect, useContext } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import { OperatorContext } from '../../contexts/OperatorContext';

const STATUS_FILTERS = [
  { value: 'new', label: 'New' },
  { value: 'handled', label: 'Handled' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: '', label: 'All' },
];

function formatDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
}

function details(request) {
  if (request.type === 'call') {
    return request.scheduled_start ? `Call on ${formatDate(request.scheduled_start)}` : 'Call';
  }
  return [request.reason, request.message].filter(Boolean).join(': ');
}

function ManageSupportRequests() {
  const { getSupportRequests, updateSupportRequestStatus } = useContext(OperatorContext);

  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState('new');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionId, setActionId] = useState('');

  async function refresh(status = statusFilter) {
    setLoading(true);
    setError('');
    try {
      const rows = await getSupportRequests(status);
      setRequests(rows);
      return true;
    } catch {
      setError('Failed to load support requests.');
      return false;
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh(statusFilter);
  }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleStatus(id, status) {
    setActionId(id);
    setError('');
    setSuccess('');
    try {
      await updateSupportRequestStatus(id, status);
      const loaded = await refresh();
      if (loaded) setSuccess(`Request marked ${status}.`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update request.');
    } finally {
      setActionId('');
    }
  }

  return (
    <div>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
        <h2 style={{ margin: 0 }}>Support Requests</h2>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Select
            size="small"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            displayEmpty
            inputProps={{ 'aria-label': 'Status filter' }}
          >
            {STATUS_FILTERS.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </Select>
          <Button variant="outlined" onClick={() => refresh()} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Box>

      {error && <p role="alert" style={{ color: '#E23844' }}>{error}</p>}
      {success && <p role="status" style={{ color: '#2E7D32' }}>{success}</p>}

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 900 }} aria-label="Support requests table">
          <TableHead>
            <TableRow>
              <TableCell>Submitted</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Organization</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Details</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} style={{ textAlign: 'center' }}>Loading...</TableCell>
              </TableRow>
            ) : requests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} style={{ textAlign: 'center' }}>No support requests.</TableCell>
              </TableRow>
            ) : (
              requests.map((request) => (
                <TableRow key={request._id}>
                  <TableCell>{formatDate(request.createdAt)}</TableCell>
                  <TableCell>{request.type}</TableCell>
                  <TableCell>{request.organization_name || request.organization_id || '—'}</TableCell>
                  <TableCell>{request.name}</TableCell>
                  <TableCell>{request.email}</TableCell>
                  <TableCell>{request.phone || '—'}</TableCell>
                  <TableCell>{details(request) || '—'}</TableCell>
                  <TableCell>{request.status}</TableCell>
                  <TableCell>
                    {request.status === 'new' && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleStatus(request._id, 'handled')}
                        disabled={Boolean(actionId)}
                      >
                        {actionId === request._id ? 'Updating...' : 'Mark handled'}
                      </Button>
                    )}
                    {request.status === 'handled' && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleStatus(request._id, 'new')}
                        disabled={Boolean(actionId)}
                      >
                        {actionId === request._id ? 'Updating...' : 'Mark new'}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}

export default ManageSupportRequests;
